import { UUID } from '@/types';
import { useIntercom } from 'react-use-intercom';
import { useDashboardId, useDatasourceId, useTimeoutToggle } from '@/hooks';
import {
  useGetEmbeddedConfigQuery,
  useRegenerateApiKeyMutation,
  useSaveEmbeddedConfigMutation,
} from '@/features/dashboard/apiSlice';
import {
  Banner,
  Body,
  Button,
  ButtonLoading,
  Link,
  Small,
  TextAction,
  TextCopy,
  Tooltip,
  TrevorIcon,
} from '@trevor/ui-components';
import React from 'react';
import { debounce } from '@/util/fp';
import { EmbedDashboardConfigurations } from '@/features/dashboard/components/EmbedDashboardConfigurations';
import { useEmbedDashboard } from '@/hooks/useEmbedDashboard';
import { getIsAdmin } from '@/store-selectors';
import { useSelector } from 'react-redux';
import { userAction } from '@/util/helpers/ajax-helpers';
import {
  ConfigureEmbedModalContainer,
  BannerLink,
  RedirectLabel,
  RedirectUrlContainer,
  VariableLabel,
  VariablesContainer,
} from './ConfigureEmbedDashboardModal.styles';

const tooltipDeleteConfig = 'tooltipDeleteConfig';
export const getTestConnectionUrl = (url: string, dashboardId: UUID) => {
  return `${url}?resource_type=dashboard&resource_id=${dashboardId}`;
};

export const startsWithHttpOrHttps = (value: string) => {
  return /(http(s?)):\/\//i.test(value);
};

const debounce1s = debounce(1000);

export const ConfigureEmbedModal = () => {
  const isAdmin = useSelector(getIsAdmin);

  return (
    <ConfigureEmbedModalContainer
      title="Configure dashboard embed"
      noDefaultFooter
    >
      {isAdmin ? (
        <ConfigureEmbedDashboardAdminContent />
      ) : (
        <ConfigureEmbedDashboardCollaboratorContent />
      )}

      <Link
        href="https://guide.trevor.io/hc/en-us/articles/10596347337245"
        target="_blank"
        iconName="book"
        onClick={() =>
          userAction('embed-dashboards-configuration-modal', {
            action: 'more-info-guide',
          })
        }
      >
        More info in Trevor guide
      </Link>
    </ConfigureEmbedModalContainer>
  );
};

const ConfigureEmbedDashboardCollaboratorContent = () => {
  return (
    <Banner severity="info">
      To configure the dashboard embed you need to contact your admin and point
      them towards this location.
    </Banner>
  );
};
const ConfigureEmbedDashboardAdminContent = () => {
  const { showNewMessage } = useIntercom();
  const datasourceId = useDatasourceId();
  const dashboardId = useDashboardId();

  const { data } = useGetEmbeddedConfigQuery({
    datasourceId,
  });
  const [regenerateKey, { isLoading: regenerateKeyLoading }] =
    useRegenerateApiKeyMutation();
  const [
    saveEmbeddedConfig,
    {
      isLoading: saveEmbeddedConfigLoading,
      isSuccess: saveEmbeddedConfigSuccess,
    },
  ] = useSaveEmbeddedConfigMutation();

  const apiKey = data?.apiKey ?? '';
  const storedRedirectUrl = data?.redirectUrl ?? '';
  const [redirectUrl, setRedirectUrl] = React.useState('');
  const [newRedirectUrl, setNewRedirectUrl] = React.useState('');
  const [savedRedirectUrl, toggleSavedRedirectUrl] = useTimeoutToggle(2000);
  const { isConfigured } = useEmbedDashboard();

  React.useEffect(() => {
    setRedirectUrl(storedRedirectUrl);
    setNewRedirectUrl(storedRedirectUrl);
  }, [storedRedirectUrl]);

  React.useEffect(() => {
    if (saveEmbeddedConfigSuccess) toggleSavedRedirectUrl();
  }, [saveEmbeddedConfigSuccess]);

  const watchRedirectUrlChange = debounce1s((value: string) => {
    setNewRedirectUrl(value);
  });

  const handleRedirectUrlChange = (value: string) => {
    setRedirectUrl(value);
    watchRedirectUrlChange(value);
  };

  const handleSaveEmbedConfig = (value?: string) => {
    saveEmbeddedConfig({
      datasourceId,
      body: { redirectUrl: value },
    });
    userAction('embed-dashboards-configuration-modal', {
      action: value ? 'redirect-url-save' : 'redirect-url-delete',
    });

    // If the embed configuration is deleted > clean redirectUrl states
    if (!value) {
      setRedirectUrl('');
      setNewRedirectUrl('');
    }
  };

  const redirectUrlError = Boolean(
    newRedirectUrl && !startsWithHttpOrHttps(newRedirectUrl),
  );

  const handleContactSupport = () => {
    showNewMessage(
      "I'm trying to configure embedded dashboards. My question is:\n\n",
    );
    userAction('embed-dashboards-configuration-modal', {
      action: 'contact-support',
    });
  };

  const handleTestConnection = () => {
    window.open(getTestConnectionUrl(redirectUrl, dashboardId), '_blank');
    userAction('embed-dashboards-configuration-modal', {
      action: 'redirect-url-test',
    });
  };

  const handleRegenerateKey = async () => {
    await regenerateKey({ datasourceId });

    userAction('embed-dashboards-configuration-modal', {
      action: 'api-key-reset',
    });
  };

  return (
    <>
      <Banner severity="warning" width="100%">
        This step requires your engineering team. <br />
        Please&nbsp;
        <BannerLink onClick={handleContactSupport}>
          contact Trevor support
        </BannerLink>
        &nbsp;if you have any questions.
        <br />
        <br />
        <BannerLink
          href="https://guide.trevor.io/hc/en-us/articles/10596347337245"
          target="_blank"
        >
          View technical documentation
        </BannerLink>
      </Banner>
      <Body variant={2}>
        Please provide a <RedirectLabel>redirect URL</RedirectLabel> that Trevor
        will redirect to if a user tries to access the dashboard. This will
        handle authorization for deciding who has access to the dashboard and
        what data they can see.
      </Body>
      <VariablesContainer>
        <VariableLabel>Redirect URL:</VariableLabel>
        <RedirectUrlContainer>
          <TextAction
            placeholder="e.g. https://yourdomain...."
            text={redirectUrl}
            loading={saveEmbeddedConfigLoading}
            actionLabel="Save"
            actionDisabled={redirectUrlError}
            error={
              redirectUrlError
                ? 'URL must include “http://” or “https://”'
                : null
            }
            onChange={handleRedirectUrlChange}
            onAction={(value) => handleSaveEmbedConfig(value)}
            actionTooltip={savedRedirectUrl ? 'Saved!' : null}
          />
          {!newRedirectUrl && (
            <Small>URL must include “http://” or “https://”</Small>
          )}
        </RedirectUrlContainer>
        <Button
          onClick={handleTestConnection}
          variant="secondary"
          disabled={!newRedirectUrl || redirectUrlError}
        >
          Test connection
        </Button>
        <ButtonLoading
          disabled={!storedRedirectUrl}
          data-tooltip-id={tooltipDeleteConfig}
          loading={saveEmbeddedConfigLoading}
          variant="secondary"
          icon={<TrevorIcon name="trash-bin" />}
          onClick={() => handleSaveEmbedConfig()}
        />
        <Tooltip id={tooltipDeleteConfig} place="top" isMultiline>
          Delete embeddable dashboards config. This will disable your current
          embed dashboards
        </Tooltip>
        <VariableLabel>API Key:</VariableLabel>
        <TextCopy
          text={apiKey}
          onCopy={() =>
            userAction('embed-dashboards-configuration-modal', {
              action: 'api-key-copy',
            })
          }
        />
        <ButtonLoading
          loading={regenerateKeyLoading}
          variant="secondary"
          onClick={handleRegenerateKey}
        >
          Reset key
        </ButtonLoading>
      </VariablesContainer>
      {isConfigured && <EmbedDashboardConfigurations />}
    </>
  );
};
